import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, Divider } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { AllocationOption, BulkInventoryItem, InventoryStatus } from 'common/modules/inventory';
import { getTypedKeys } from 'common/utils/objects';
import { InventoryStatuses } from 'common/modules/inventory/constants';
import StatusItem from '../components/StatusItem';
import EditModal from './EditModal';
import AddItemsModal from './AddItemsModal';
import { useTranslation } from 'services/localization';
import { useFormContext, useWatch } from 'react-hook-form';
import { isEqual } from 'lodash';

interface Props {
	item: BulkInventoryItem;
	statuses: BulkInventoryItem['statuses'];
	quantity: BulkInventoryItem['quantity'];
	allocations: BulkInventoryItem['allocations'];
}

const inventoryStatusesList = getTypedKeys(InventoryStatuses);

const BulkStatus = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();

	const [statusToEdit, setStatusToEdit] = useState<InventoryStatus | null>(null);
	const [addItemsOpen, setAddItemsOpen] = useState<boolean>(false);

	const { control, register, setValue } = useFormContext();
	const bulkStatuses: BulkInventoryItem['statuses'] = useWatch({
		control,
		name: 'statuses',
		defaultValue: props.statuses,
	});

	const totalQuantity: BulkInventoryItem['quantity'] = useWatch({
		control,
		name: 'quantity',
		defaultValue: props.quantity,
	});

	const allocations: BulkInventoryItem['allocations'] = useWatch({
		control,
		name: 'allocations',
		defaultValue: props.allocations,
	});

	const itemStatusesWithQuantity = bulkStatuses;
	const existingItemStatuses = inventoryStatusesList.filter(
		(status) =>
			itemStatusesWithQuantity[status] !== undefined && itemStatusesWithQuantity[status] !== 0,
	);

	useEffect(() => {
		register('statuses');
		register('quantity');
		register('allocations');
	}, [register]);

	const handleEditStatusClick = (status: InventoryStatus) => {
		setStatusToEdit(status);
	};

	const handleStatusUpdate = async (
		statusToAddTo: InventoryStatus,
		changedQuantity: number,
		allocationToChange: AllocationOption,
	) => {
		if (statusToEdit === null) return;

		if (statusToEdit !== statusToAddTo) {
			const statusToRemoveFrom = statusToEdit;
			const statusToRemoveFromQuantity = itemStatusesWithQuantity[statusToRemoveFrom];
			if (statusToRemoveFromQuantity === undefined) {
				setStatusToEdit(null);
				return;
			}
			const statusToRemoveFromNewQuantity =
				statusToRemoveFromQuantity === null ? null : statusToRemoveFromQuantity - changedQuantity;
			const statusToAddToQuantity = itemStatusesWithQuantity[statusToAddTo];
			const statusToAddToNewQuantity =
				statusToAddToQuantity === null ? null : (statusToAddToQuantity ?? 0) + changedQuantity;

			const updatedValue = {
				...bulkStatuses,
				[statusToRemoveFrom]: statusToRemoveFromNewQuantity,
				[statusToAddTo]: statusToAddToNewQuantity,
			};

			setValue('statuses', updatedValue, { shouldDirty: true });
		}

		if (statusToAddTo === 'IN_USE') {
			if (statusToEdit === statusToAddTo) {
				const allocationTotal = Object.values(allocations).reduce((a, v) => (a += v), 0);
				const newAllocationTotal = Math.min(
					(allocations[allocationToChange] ?? 0) + changedQuantity,
					allocationTotal,
				);
				const otherAllocationTotal = Math.max(0, allocationTotal - newAllocationTotal);

				const updatedAllocations = {
					rental: allocationToChange === 'rental' ? newAllocationTotal : otherAllocationTotal,
					sales: allocationToChange === 'sales' ? newAllocationTotal : otherAllocationTotal,
				};

				if (!isEqual(updatedAllocations, props.allocations)) {
					setValue('allocations', updatedAllocations, { shouldDirty: true });
				}
			} else {
				const updatedAllocations = {
					...allocations,
					[allocationToChange]: (allocations[allocationToChange] ?? 0) + changedQuantity,
				};
				if (!isEqual(updatedAllocations, props.allocations)) {
					setValue('allocations', updatedAllocations, { shouldDirty: true });
				}
			}
		} else if (statusToEdit === 'IN_USE') {
			const { updatedAllocations } = Object.entries(allocations).reduce(
				(res, [key, allocationAmount]) => {
					const amountToRemove = Math.max(
						0,
						res.totalToRemove > allocationAmount ? allocationAmount : res.totalToRemove,
					);
					if (!!res.updatedAllocations && !!res.updatedAllocations[allocationToChange]) {
						res.updatedAllocations[allocationToChange]! -= amountToRemove;
					}
					res.totalToRemove -= amountToRemove;
					return res;
				},
				{ updatedAllocations: { ...allocations }, totalToRemove: changedQuantity },
			);

			if (!isEqual(updatedAllocations, props.allocations)) {
				setValue('allocations', updatedAllocations, { shouldDirty: true });
			}
		}

		setStatusToEdit(null);
	};

	const handleDeleteItems = (statusToDelete: InventoryStatus, quantity: number) => {
		if (statusToDelete === 'IN_USE') {
			const { updatedAllocations } = Object.entries(allocations).reduce(
				(res, [key, val]) => {
					const amountToRemove = res.tot > val ? val : res.tot;
					res.updatedAllocations[key] -= amountToRemove;
					res.tot -= amountToRemove;
					return res;
				},
				{ updatedAllocations: { ...allocations }, tot: quantity },
			);

			if (!isEqual(updatedAllocations, props.allocations)) {
				setValue('allocations', updatedAllocations, { shouldDirty: true });
			}
		}

		const statusToDeleteFromQuantity = itemStatusesWithQuantity[statusToDelete];
		const newQuantity = !!statusToDeleteFromQuantity ? statusToDeleteFromQuantity - quantity : 0;

		const updatedValue = {
			...bulkStatuses,
			[statusToDelete]: newQuantity >= 0 ? newQuantity : 0,
		};

		setValue('statuses', updatedValue, { shouldDirty: true });
		setValue('quantity', totalQuantity - quantity, { shouldDirty: true });
		setStatusToEdit(null);
	};

	const handleAddItems = async (
		statusToAdd: InventoryStatus,
		quantity: number,
		allocationToAdd: AllocationOption,
	) => {
		const statusToAddQuantity = itemStatusesWithQuantity[statusToAdd];
		const statusToAddNewQuantity =
			statusToAddQuantity === null ? null : (statusToAddQuantity ?? 0) + quantity;

		const updatedValue = {
			...bulkStatuses,
			[statusToAdd]: statusToAddNewQuantity,
		};

		if (statusToAdd === 'IN_USE') {
			const updatedAllocations = {
				...allocations,
				[allocationToAdd]: (allocations[allocationToAdd] ?? 0) + quantity,
			};
			setValue('allocations', updatedAllocations, { shouldDirty: true });
		}

		setValue('statuses', updatedValue, { shouldDirty: true });
		setValue('quantity', totalQuantity + quantity, { shouldDirty: true });
		setAddItemsOpen(false);
	};

	const handleCancelStatusEdit = () => {
		setStatusToEdit(null);
	};

	return (
		<>
			<Box className={classes.statusContainer}>
				{existingItemStatuses.map((status, index) => {
					const lastElement = existingItemStatuses.length - 1 === index;
					return (
						<div key={`${status}_${index}`}>
							<StatusItem
								status={status}
								quantity={itemStatusesWithQuantity[status]!}
								onEdit={handleEditStatusClick}
								allocations={allocations}
							/>
							{!lastElement && <Divider />}
						</div>
					);
				})}
			</Box>
			<Box mt={2} />
			<Box display="flex" alignItems="center" justifyContent="space-between">
				<Typography variant="body1">
					{t('common:count.total')}: {totalQuantity}
				</Typography>
				<Button variant="outlined" color="primary" onClick={() => setAddItemsOpen(true)}>
					+ {t('inventory.article.itemsAddBtn', 'Add items')}
				</Button>
			</Box>
			{!!statusToEdit && (
				<EditModal
					status={statusToEdit}
					quantity={itemStatusesWithQuantity[statusToEdit] ?? null}
					allocations={allocations}
					onUpdate={handleStatusUpdate}
					onDelete={handleDeleteItems}
					onCancel={handleCancelStatusEdit}
				/>
			)}
			{!!addItemsOpen && (
				<AddItemsModal onSave={handleAddItems} onCancel={() => setAddItemsOpen(false)} />
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		statusContainer: {
			borderRadius: 4,
			backgroundColor: theme.palette.background.secondary,
			border: `1px solid ${theme.palette.border.inputOutline}`,
			'&:empty': {
				border: 'none',
			},
		},
	}),
);

export default BulkStatus;
